(() => {
  if (customElements.get('klaviyo-bis')) return;

  class KlaviyoBis extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.placeTrigger();
        return;
      }
      this.initialized = true;
      this.form = this.querySelector('[data-bis-form]');
      this.email = this.form.querySelector('[name="email"]');
      this.submitButton = this.querySelector('[data-bis-submit]');
      this.message = this.querySelector('[data-bis-message]');
      this.openButton = this.querySelector('[data-bis-open]');
      this.dialog = this.querySelector('dialog');
      this.placeTrigger();
      this.openButton.addEventListener('click', () => {
        this.dialog.showModal();
        this.openButton.setAttribute('aria-expanded', 'true');
        this.email.focus();
      });
      this.querySelector('[data-bis-close]').addEventListener('click', () => this.dialog.close());
      this.dialog.addEventListener('close', () => {
        this.openButton.setAttribute('aria-expanded', 'false');
        if (this.openButton.isConnected) this.openButton.focus();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.subscribe();
      });
      this.hidden = false;
    }

    placeTrigger() {
      const slot = this.closest('product-form')?.querySelector(`[data-bis-trigger-slot="${CSS.escape(this.dataset.blockId)}"]`);
      (slot || this).append(this.openButton);
    }

    disconnectedCallback() {
      this.controller?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.openButton?.remove();
    }

    async post(resource, data, signal) {
      const response = await fetch(`https://a.klaviyo.com/client/${resource}?company_id=${encodeURIComponent(this.dataset.publicKey)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', revision: '2026-07-15' },
        body: JSON.stringify({ data }),
        signal
      });
      if (response.status !== 202) throw new Error('Klaviyo did not accept the request');
    }

    async subscribe() {
      if (this.busy || this.succeeded) return;
      this.email.value = this.email.value.trim();
      if (!this.form.reportValidity()) return;
      // Capture the variant and context before either asynchronous request.
      const variantId = this.dataset.variantId;
      if (!/^\d+$/.test(variantId)) return;
      const email = this.email.value;
      const url = new URL(this.dataset.productUrl, location.origin);
      url.searchParams.set('variant', variantId);
      const context = {
        market: this.dataset.market,
        language: this.dataset.language,
        product_url: url.href
      };
      this.busy = true;
      this.submitButton.disabled = true;
      this.email.readOnly = true;
      this.form.setAttribute('aria-busy', 'true');
      this.message.textContent = this.dataset.pending;
      this.controller = new AbortController();
      const timeout = setTimeout(() => this.controller.abort(), 15000);
      try {
        // Persist context independently of Klaviyo's optional onsite tracking script.
        // Per-variant properties preserve context when another product is subscribed to.
        await this.post('profiles', {
          type: 'profile',
          attributes: {
            email,
            properties: {
              bis_market: context.market,
              bis_language: context.language,
              bis_product_url: context.product_url,
              [`bis_context_${variantId}`]: context
            }
          }
        }, this.controller.signal);
        if (!this.isConnected) return;
        await this.post('back-in-stock-subscriptions', {
          type: 'back-in-stock-subscription',
          attributes: { channels: ['EMAIL'], profile: { data: { type: 'profile', attributes: { email } } } },
          relationships: { variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } } }
        }, this.controller.signal);
        if (!this.isConnected) return;
        this.message.textContent = this.dataset.success;
        this.succeeded = true;
      } catch (error) {
        if (this.isConnected) this.message.textContent = this.dataset.error;
      } finally {
        clearTimeout(timeout);
        this.busy = false;
        this.submitButton.disabled = !!this.succeeded;
        this.email.readOnly = !!this.succeeded;
        this.form.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('klaviyo-bis', KlaviyoBis);
})();
